import React, { useEffect, useState } from 'react';
import {
  Circle,
  Group,
  Image as KonvaImage,
  Layer,
  Line,
  Rect,
  Stage,
  Text,
} from 'react-konva';

type CanvasGradient = {
  type: 'linear';
  from: string;
  to: string;
  direction?: 'vertical' | 'horizontal' | 'diagonal';
};

type CanvasElement = {
  id: string;
  type: 'text' | 'rect' | 'circle' | 'line' | 'image';
  x: number;
  y: number;
  width: number;
  height: number;
  rotation?: number;
  zIndex?: number;
  opacity?: number;
  fill?: string;
  fillGradient?: CanvasGradient;
  stroke?: string;
  strokeWidth?: number;
  cornerRadius?: number;
  text?: string;
  fontFamily?: string;
  fontSize?: number;
  fontWeight?: number | string;
  fontStyle?: 'normal' | 'italic';
  align?: 'left' | 'center' | 'right';
  letterSpacing?: number;
  lineHeight?: number;
  src?: string;
  objectFit?: 'cover' | 'fill' | 'contain';
  flipX?: boolean;
  flipY?: boolean;
  textDecoration?: 'none' | 'underline';
};

export type CanvasDesignDocument = {
  version: 1;
  canvas: {
    width: number;
    height: number;
    background: string;
    backgroundGradient?: CanvasGradient;
  };
  elements: CanvasElement[];
};

export type CanvasDesignPlayerContent = {
  id?: string;
  name?: string;
  width?: number;
  height?: number;
  thumbnailUrl?: string;
  designJson?: CanvasDesignDocument;
};

type CanvasDesignRendererProps = {
  content: CanvasDesignPlayerContent | null;
  objectFit?: 'fill' | 'contain';
};

function gradientFill(
  gradient: CanvasGradient | undefined,
  width: number,
  height: number
) {
  if (!gradient) return {};
  const direction = gradient.direction || 'vertical';
  const end =
    direction === 'horizontal'
      ? { x: width, y: 0 }
      : direction === 'diagonal'
        ? { x: width, y: height }
        : { x: 0, y: height };
  return {
    fillLinearGradientStartPoint: { x: 0, y: 0 },
    fillLinearGradientEndPoint: end,
    fillLinearGradientColorStops: [0, gradient.from, 1, gradient.to],
  };
}

function coverCrop(image: HTMLImageElement, boxW: number, boxH: number) {
  const iw = image.naturalWidth || image.width;
  const ih = image.naturalHeight || image.height;
  if (!iw || !ih) return { x: 0, y: 0, width: boxW, height: boxH };
  const scale = Math.max(boxW / iw, boxH / ih);
  const width = iw * scale;
  const height = ih * scale;
  return {
    x: (boxW - width) / 2,
    y: (boxH - height) / 2,
    width,
    height,
  };
}

function containFit(image: HTMLImageElement, boxW: number, boxH: number) {
  const iw = image.naturalWidth || image.width;
  const ih = image.naturalHeight || image.height;
  if (!iw || !ih) return { x: 0, y: 0, width: boxW, height: boxH };
  const scale = Math.min(boxW / iw, boxH / ih);
  const width = iw * scale;
  const height = ih * scale;
  return {
    x: (boxW - width) / 2,
    y: (boxH - height) / 2,
    width,
    height,
  };
}

function konvaFontStyle(element: CanvasElement): string {
  const italic = element.fontStyle === 'italic';
  const weight = element.fontWeight;
  const bold =
    weight === 'bold' ||
    (typeof weight === 'number' && weight >= 600) ||
    (typeof weight === 'string' && Number(weight) >= 600);
  if (italic && bold) return 'italic bold';
  if (italic) return 'italic';
  if (bold) return 'bold';
  return 'normal';
}

function useHtmlImage(src?: string) {
  const [image, setImage] = useState<HTMLImageElement | null>(null);

  useEffect(() => {
    if (!src) {
      setImage(null);
      return;
    }
    let cancelled = false;
    const load = (useCors: boolean) => {
      const element = new window.Image();
      if (useCors) element.crossOrigin = 'anonymous';
      element.onload = () => {
        if (!cancelled) setImage(element);
      };
      element.onerror = () => {
        if (cancelled) return;
        if (useCors) {
          load(false);
          return;
        }
        setImage(null);
      };
      element.src = src;
      if (element.complete && element.naturalWidth > 0 && !cancelled) {
        setImage(element);
      }
    };
    load(true);
    return () => {
      cancelled = true;
    };
  }, [src]);

  return image;
}

function useViewport() {
  const [size, setSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  useEffect(() => {
    const onResize = () =>
      setSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return size;
}

function CanvasNode({ element }: { element: CanvasElement }) {
  const image = useHtmlImage(element.type === 'image' ? element.src : undefined);
  const common = {
    id: element.id,
    x: element.x,
    y: element.y,
    width: element.width,
    height: element.height,
    rotation: element.rotation || 0,
    opacity: element.opacity ?? 1,
    listening: false,
  };

  if (element.type === 'rect') {
    const hasFill = Boolean(element.fillGradient || element.fill);
    return (
      <Rect
        {...common}
        fillEnabled={hasFill}
        fill={element.fillGradient ? undefined : element.fill}
        {...gradientFill(element.fillGradient, element.width, element.height)}
        stroke={element.stroke}
        strokeWidth={element.strokeWidth}
        cornerRadius={element.cornerRadius}
        perfectDrawEnabled={false}
      />
    );
  }

  if (element.type === 'circle') {
    const radius = Math.min(element.width, element.height) / 2;
    return (
      <Group {...common}>
        <Circle
          x={element.width / 2}
          y={element.height / 2}
          radius={radius}
          fill={element.fill}
          stroke={element.stroke}
          strokeWidth={element.strokeWidth}
          listening={false}
          perfectDrawEnabled={false}
        />
      </Group>
    );
  }

  if (element.type === 'line') {
    return (
      <Group {...common}>
        <Line
          points={[0, 0, element.width, element.height]}
          stroke={element.stroke || element.fill || '#ffffff'}
          strokeWidth={element.strokeWidth || 2}
          listening={false}
          perfectDrawEnabled={false}
        />
      </Group>
    );
  }

  if (element.type === 'image') {
    const radius = element.cornerRadius || 0;
    const fit = element.objectFit || 'cover';
    const crop = !image
      ? { x: 0, y: 0, width: element.width, height: element.height }
      : fit === 'contain'
        ? containFit(image, element.width, element.height)
        : fit === 'cover'
          ? coverCrop(image, element.width, element.height)
          : { x: 0, y: 0, width: element.width, height: element.height };
    return (
      <Group
        {...common}
        clipFunc={ctx => {
          const r = Math.min(radius, element.width / 2, element.height / 2);
          ctx.beginPath();
          if (r > 0) {
            ctx.moveTo(r, 0);
            ctx.lineTo(element.width - r, 0);
            ctx.quadraticCurveTo(element.width, 0, element.width, r);
            ctx.lineTo(element.width, element.height - r);
            ctx.quadraticCurveTo(
              element.width,
              element.height,
              element.width - r,
              element.height
            );
            ctx.lineTo(r, element.height);
            ctx.quadraticCurveTo(0, element.height, 0, element.height - r);
            ctx.lineTo(0, r);
            ctx.quadraticCurveTo(0, 0, r, 0);
          } else {
            ctx.rect(0, 0, element.width, element.height);
          }
          ctx.closePath();
        }}
      >
        <Group
          x={element.flipX ? element.width : 0}
          y={element.flipY ? element.height : 0}
          scaleX={element.flipX ? -1 : 1}
          scaleY={element.flipY ? -1 : 1}
          listening={false}
        >
          {image ? (
            <KonvaImage
              image={image}
              x={crop.x}
              y={crop.y}
              width={crop.width}
              height={crop.height}
              listening={false}
              perfectDrawEnabled={false}
            />
          ) : (
            <Rect
              width={element.width}
              height={element.height}
              fill="#334155"
              listening={false}
              perfectDrawEnabled={false}
            />
          )}
        </Group>
      </Group>
    );
  }

  const underlineY = (element.fontSize || 24) * (element.lineHeight || 1.15);
  return (
    <Group {...common}>
      <Text
        width={element.width}
        height={element.height}
        text={element.text || ''}
        fill={element.fill}
        fontFamily={element.fontFamily || 'Montserrat'}
        fontSize={element.fontSize || 24}
        fontStyle={konvaFontStyle(element)}
        align={element.align || 'left'}
        letterSpacing={element.letterSpacing || 0}
        lineHeight={element.lineHeight || 1.15}
        wrap="word"
        listening={false}
        perfectDrawEnabled={false}
      />
      {element.textDecoration === 'underline' ? (
        <Line
          points={[0, underlineY, element.width, underlineY]}
          stroke={element.fill || '#ffffff'}
          strokeWidth={Math.max(1, (element.fontSize || 24) / 18)}
          listening={false}
        />
      ) : null}
    </Group>
  );
}

const CanvasDesignRenderer: React.FC<CanvasDesignRendererProps> = ({
  content,
  objectFit = 'contain',
}) => {
  const viewport = useViewport();
  const design = content?.designJson;
  if (!design?.canvas || !Array.isArray(design.elements)) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-black text-white">
        No canvas design
      </div>
    );
  }

  const canvas = design.canvas;
  const scaleW = viewport.width / canvas.width;
  const scaleH = viewport.height / canvas.height;
  const scale = objectFit === 'fill' ? Math.max(scaleW, scaleH) : Math.min(scaleW, scaleH);
  const stageWidth = canvas.width * scale;
  const stageHeight = canvas.height * scale;
  const sorted = [...design.elements].sort(
    (a, b) => (a.zIndex || 0) - (b.zIndex || 0)
  );

  return (
    <div className="fixed inset-0 h-screen w-screen bg-black overflow-hidden flex items-center justify-center">
      <Stage
        width={Math.max(1, stageWidth)}
        height={Math.max(1, stageHeight)}
        scaleX={scale}
        scaleY={scale}
        listening={false}
      >
        <Layer listening={false}>
          <Rect
            x={0}
            y={0}
            width={canvas.width}
            height={canvas.height}
            fill={canvas.backgroundGradient ? undefined : canvas.background}
            {...gradientFill(
              canvas.backgroundGradient,
              canvas.width,
              canvas.height
            )}
            listening={false}
          />
          {sorted.map(element => (
            <CanvasNode key={element.id} element={element} />
          ))}
        </Layer>
      </Stage>
    </div>
  );
};

export default CanvasDesignRenderer;
